/**
 * Buyer-facing dashboard copy, flywheel front door (proof + urgency + seal).
 *
 * Flywheel overview: enforcement health, audit trail, export, trust seal.
 * Value-moment live-check sandbox is off the page (SHOW_VALUE_MOMENT_SANDBOX).
 * trust seal, and gate-correct upgrade CTAs (prices + Enterprise for export;
 * Developer self-serve checkout for volume). List prices are anchors, not claimed ACV.
 */

import { retentionPeriodLabel } from './retentionPresets';

/** "records kept N days, then expire". */
function retentionClause(days: number): string {
  return `records kept ${retentionPeriodLabel(days)}, then expire`;
}

export const DASHBOARD_COPY = {
  /** Overview key card: copy the key in use, create a new one, try it. */
  apiKeyCopy: 'Copy key',
  apiKeyCopied: 'Copied',
  /** Shown on any copy control when the browser refused the clipboard write (DASH-020). */
  copyFailed: 'Copy failed',
  /** Primary action when a key exists: mint a new one, then offer to revoke the old. */
  apiKeyRotate: 'Rotate key',
  /** Primary action when no key exists yet. */
  apiKeyCreate: 'Create key',
  apiKeyCreating: 'Creating...',
  /** Keys are created without a name form; one label fits the one-key flow. */
  apiKeyDefaultName: 'API Key',
  apiKeyNone: 'No API key yet. Create one to send your first check.',
  /** After the only live key was revoked: say so, then offer Create. */
  apiKeyRevokedNone: 'Key revoked. Create a new one to send checks again.',
  apiKeyShownOnceHint: 'Shown once at creation. Rotate to get a new value.',
  apiKeyMeta: (created: string, lastUsed: string | null) =>
    lastUsed ? `Created ${created} · Last used ${lastUsed}` : `Created ${created} · Never used`,
  /** Shown under the heading when the tenant holds more than one active key. */
  apiKeyActiveCount: (n: number) => `Newest of ${n} active keys`,
  /** Inside the reveal after a rotate: the old key is still live until revoked. */
  apiKeyPreviousStillWorksBefore: 'Your previous key',
  apiKeyPreviousStillWorksAfter: 'still works.',
  apiKeyRevokePrevious: 'Revoke previous key',
  apiKeyRevokingPrevious: 'Revoking...',
  apiKeyKeepBoth: 'Keep both for now',
  apiKeyPreviousRevoked: 'Previous key revoked.',
  apiKeyCreateError: 'Could not create a key. Try again.',
  apiEndpointCopy: 'Copy',
  /** Sandbox under the key: try a case against the tenant's own enforcement. */
  sandboxHeading: 'Try a request',
  sandboxHint: 'Runs the same check as production. Nothing here is logged.',
  sandboxTag: 'Sandbox · not logged',
  sandboxTextLabel: 'Request text',
  sandboxRun: 'Run check',
  sandboxRunning: 'Checking…',
  sandboxBlocked: 'Blocked by a rule',
  sandboxAllowed: 'Allowed through',
  sandboxAllowedNote: 'No rule fired for this text.',
  sandboxRule: 'Rule',
  sandboxLaw: 'Law',
  sandboxDecidedIn: (ms: number) => `Decided in ${ms} ms`,
  sandboxError: 'Could not run the check. Try again.',
  /**
   * Page heading per section: every section used to open with the same
   * "What your AI did" title (Leo, 2026-09-23). Status was renamed on
   * 2026-10-10: the record proves which rule ran, not what the AI did.
   */
  sectionHeadings: {
    status: {
      title: 'What your rules caught',
      subhead:
        'Every check you send is logged. The rule and the law are named when a phrase rule or the injection check fired. A wider-review block does not cite an article.',
    },
    usage: {
      title: 'Usage',
      subhead: "How many checks you have run and how close you are to your plan's daily limit.",
    },
    trust: {
      title: 'AI Trust Center',
      subhead: 'Show customers the rules your AI runs under.',
    },
    audit: {
      title: 'Audit Trail',
      subhead:
        'Review the outputs flagged for a person, and export your decision records as CSV or JSON for an auditor.',
    },
    'api-key': {
      title: 'API Key',
      subhead: 'The key your agents use to call ComplyEdge. Create a new one or revoke an old one.',
    },
  } as const,

  signOut: 'Sign out',
  planLoading: 'Loading plan…',
  navDocs: 'Docs',
  /** Header section links: one page each, in this order. */
  navSections: {
    status: 'Status',
    usage: 'Usage',
    trust: 'AI Trust Center',
    audit: 'Audit Trail',
    'api-key': 'API Key',
  } as const,
  usageHeading: 'Where you are against your plan',
  apiKeysLoadError: 'Your API keys could not be loaded. Reload the page to try again.',
  apiKeysOthersHeading: 'Other active keys',
  apiKeysOthersSubhead: 'Older keys that still work. Revoke the ones nothing uses.',
  /** Same copy as the website footer partial (apps/website/templates/footer.html). */
  footer: {
    tagline: 'Blocks AI requests that break EU law.',
    subline: "Every check is logged, never the prompt or the AI's answer.",
    product: 'Product',
    company: 'Company',
    legal: 'Legal',
    disclaimer:
      'Disclaimer: ComplyEdge provides compliance tooling, not legal advice. Always consult legal counsel for specific regulatory requirements.',
  },
  accountMenu: 'Account menu',
  planPill: (plan: 'free' | 'developer' | 'enterprise') =>
    ({ free: 'Free plan', developer: 'Developer plan', enterprise: 'Enterprise plan' })[plan],
  planPillUpgrade: 'Upgrade',
  menuAccount: 'Account',
  menuBilling: 'Billing',
  menuBillingOpening: 'Opening billing…',
  menuBillingError: 'Billing could not open. Try again, or use Contact support.',
  /** The error sentence split around its Contact support button (same words as menuBillingError). */
  menuBillingErrorLead: 'Billing could not open. Try again, or use ',
  menuBillingErrorLink: 'Contact support',
  menuBillingErrorTail: '.',
  menuPlans: 'Plans & billing',
  planModalClose: 'Close plans',

  // Section 1: enforcement health
  healthHeadlineWaiting: 'Waiting for your first check.',
  healthHeadlineLive: (ago: string) => `Live. Last check ${ago}.`,
  healthHeadlineQuiet: (ago: string) => `Quiet. Last check ${ago}.`,
  healthHeadlineStale: (days: number) => `No checks for ${days.toLocaleString()} days.`,
  healthSubWaiting: 'Send one check with your API key and this card comes alive.',
  healthSubLive:
    'Prompts are checked before they reach your AI, answers before your user sees them. A phrase-rule block carries the article it cites. A wider-review block does not. Every check is logged.',
  healthSubQuiet: 'No checks in the last 24 hours. Fine if that is expected.',
  healthSubStale: 'Confirm your integration is still sending checks.',
  healthMetricChecks24h: 'Requests checked · last 24h',
  healthMetricBlocks30d: 'Blocked · last 30 days',
  healthMetricTopRule: 'Most common reason',
  healthNothingBlocked: 'Nothing blocked yet',
  /** Blocks in the window, none naming a rule: all were fail-closed refusals (DASH-027). */
  healthNoRuleNamed: 'Refused, not evaluated',
  healthSeeDecision: 'Open the decision',
  decisionModalEyebrow: 'Decision record',
  decisionModalClose: 'Close decision',
  // Closing a message dialog with typed text (DASH-023).
  draftDiscardTitle: 'Discard this message?',
  draftDiscardHint: 'What you typed is lost when this dialog closes. Nothing has been sent.',
  draftDiscardYes: 'Discard message',
  draftDiscardNo: 'Keep writing',
  draftDiscardClose: 'Close this question and keep writing',
  healthLoading: 'Loading enforcement status…',
  healthError: 'Unable to load enforcement status',

  // Section 3: trust: one block, two cards: the seal (+ embed) and the
  // AI Trust Center page (+ preview / publish). Voice matches the homepage
  // seal block (2026-09-18); split into two cards on Leo's ask (2026-09-19).
  trustSealCardTitle: 'Enforcement Seal',
  // Served with max-age=300 (main.py badge route): it moves within 5 minutes
  // of new checks, not with each one (DASH-042).
  trustSealCardBlurb:
    'A badge for your website showing your enforcement status and, once volume builds, how many checks ran. It refreshes within 5 minutes.',
  trustCenterCardTitle: 'Public page',
  trustCenterCardBlurb: 'What is enforced, how many checks ran, and when.',
  trustCenterHost: 'trust.complyedge.io/',
  trustCenterUrl: (slug: string | null) => `trust.complyedge.io/${slug || 'your-company'}`,
  /** One switch publishes the page and the seal together (Leo, 2026-09-25). */
  trustSwitchLabel: 'Visible to customers',
  trustSwitchOn: 'The page is public and the seal shows on your website.',
  trustSwitchOff: 'Only you can see the seal and the page.',
  trustSwitchOnShort: 'On',
  trustSwitchOffShort: 'Off',
  trustExampleLink: 'Open a live example',
  trustPreviewPageLink: 'Preview page',
  trustCopyEmbed: 'Copy snippet',
  trustCopied: 'Copied',
  trustOpenPage: 'Open page',
  trustEmbedLabel: 'Embed snippet',
  trustEmbedPendingNote:
    // Chosen at sign-up; "Create key" here never asks for a region (DASH-042).
    'Paste it anytime. It appears once the switch above is on. The host in this snippet is your account region, chosen when you created your account.',
  trustLoading: 'Loading trust…',
  /** Homepage showcase: opt-in, off by default (Leo, 2026-09-28). */
  showcaseLabel: 'Show on the ComplyEdge homepage',
  showcaseBlurb: 'Your name and seal, listed on complyedge.io.',
  // Per-container list cache (600 s) plus max-age=300 on the route (DASH-042).
  showcaseOnNote: 'You appear on the homepage within 15 minutes.',
  /** Tooltips on the locked switch (Leo, 2026-09-29). */
  showcaseNeedsName: 'Update your company name.',
  showcaseViewOnHomepage: 'See it on the homepage',
  showcaseNeedsPage: 'You need to enable "Visible to customers" first.',
  /** Turning the showcase on asks first (Leo, 2026-09-29); turning it off does not. */
  showcaseConfirmTitle: 'Show your seal on the ComplyEdge homepage?',
  showcaseConfirmHint:
    'Your company name and Enforcement Seal become publicly visible on the ComplyEdge homepage, complyedge.io, for anyone who visits, linked to your public page. You can turn it off at any time.',
  showcaseConfirmYes: 'Show on homepage',
  showcaseConfirming: 'Turning on…',
  showcaseConfirmNo: 'Cancel',
  showcaseConfirmClose: 'Close without changing',
  showcaseError: 'Could not turn on the showcase. Try again.',

  // Secondary: recent decisions feed.
  // 2026-09-18 redesign: a row is a sentence (decision · agent · why · when);
  // the drill-down answers three questions: what happened, why, proof.
  recentActivityEyebrow: 'Audit trail',
  recentActivity: 'Recent decisions',
  recentActivitySubhead:
    "Your agents' newest decisions. Open one to see why it was decided and the proof.",
  recentActivityEmpty:
    'No decisions yet. Send a check with your API key to write the first audit event.',
  decisionFilterLabel: 'Filter by decision',
  decisionFilterAll: 'All',
  decisionFilterAllowed: 'Allowed',
  decisionFilterBlocked: 'Blocked',
  recentActivityEmptyFiltered: (kind: 'allowed' | 'blocked') =>
    kind === 'blocked'
      ? 'No blocked decisions in the loaded window.'
      : 'No allowed decisions in the loaded window.',
  /** Opens the Audit Trail page (the whole trail for a period, every agent).
   *  "Full" says it is not the filtered rows above. */
  recentActivityExportLink: 'Export full audit trail',
  agentFilterLabel: 'Filter by agent',
  agentFilterAll: 'All agents',
  agentFilterUnavailable: 'Could not load the agent list.',
  /** Empty state when an agent is chosen; kind is the decision filter. */
  recentActivityEmptyForAgent: (agent: string, kind: 'all' | 'allowed' | 'blocked') =>
    kind === 'all'
      ? `No decisions from ${agent} in the loaded window.`
      : `No ${kind} decisions from ${agent} in the loaded window.`,
  recentActivityColDecision: 'Decision',
  recentActivityColAgent: 'Agent',
  recentActivityColWhy: 'Why',
  recentActivityColWhen: 'When',
  recentActivityNoRule: 'no rule fired',
  recentActivityModelAllowed: 'model check allowed it',
  /** Blocked with no rule: neither layer could evaluate it (decisionKind). */
  recentActivityNotEvaluated: 'not evaluated, refused',
  /** Allowed after model review cleared the rules that matched. */
  recentActivityCleared: 'cleared by model review',
  // Retention is the tenant's live setting (GET /v1/tenant/retention), never a
  // literal: the 184-day default is a ComplyEdge guarantee, not a statute, and
  // Enterprise can lengthen it to 7 or 11 years (adcr_record.allowed_retention_days).
  // null = not loaded yet; say nothing about expiry rather than guess.
  recentActivityExpandHint: (retentionDays: number | null) =>
    retentionDays == null
      ? 'click any decision for the full record'
      : `click any decision for the full record · ${retentionClause(retentionDays)}`,
  recentActivityShowing: (shown: number, total: number) => `Showing ${shown} of ${total}`,
  recentActivityShowMore: (n: number) => `Show ${n} more`,
  // Drill-down detail panel
  detailWhatHappened: 'What happened',
  detailWhy: 'Why',
  detailProof: 'Proof',
  detailDecisionBlocked: 'Blocked by a rule',
  detailDecisionInjection: 'Blocked by the injection check',
  detailDecisionWider: 'Blocked by the wider review',
  detailDecisionAllowed: 'Allowed, no rule fired',
  detailDecisionNotEvaluated: 'Refused: the engine could not evaluate it',
  detailDecisionCleared: 'Allowed: model review cleared the rules that matched',
  /** One sentence built from the record; never from anything the record lacks. */
  detailSentence: (o: {
    agent: string;
    blocked: boolean;
    latency: string | null;
    jurisdiction: string;
    model: string | null;
    /** The record's stored `direction`. Absent on old rows: say neither. */
    direction?: string | null;
    /** False when neither layer could evaluate (decisionKind 'notEvaluated'). */
    evaluated?: boolean;
    /** True when model review cleared rules that matched (overridden_hits). */
    cleared?: boolean;
    /** injection or wider: the block id is a model check, not a phrase rule. */
    source?: 'injection' | 'wider' | 'rule';
  }) => {
    // No rule ran and no verdict was made: never "by a phrase rule".
    if (o.evaluated === false) {
      return `${o.agent} sent text for a check. ComplyEdge could not evaluate it, so it made no decision and did not let it through.`;
    }
    const how =
      o.source === 'injection'
        ? o.model
          ? `by the injection check (${o.model})`
          : 'by the injection check'
        : o.source === 'wider'
          ? o.model
            ? `by the wider review (${o.model})`
            : 'by the wider review'
          : o.model
            ? o.cleared
              ? `after model review (${o.model})`
              : `after a model check (${o.model})`
            : `by a phrase rule (${o.jurisdiction} corpus)`;
    const took = o.latency ? `, in ${o.latency},` : '';
    // A prompt check runs before the model; an output check runs on the
    // model's answer, after it. "Before it reached the model" was said for
    // every block regardless (2026-10-10).
    if (o.direction === 'prompt') {
      return o.blocked
        ? `${o.agent} sent a request. ComplyEdge blocked it before it reached the model${took} ${how}.`
        : `${o.agent} sent a request. ComplyEdge checked it${took} ${how} and let it through.`;
    }
    if (o.direction === 'output') {
      return o.blocked
        ? `The model answered ${o.agent}. ComplyEdge blocked the answer before it was returned${took} ${how}.`
        : `The model answered ${o.agent}. ComplyEdge checked the answer${took} ${how} and let it through.`;
    }
    return o.blocked
      ? `${o.agent} sent text for a check. ComplyEdge blocked it${took} ${how}.`
      : `${o.agent} sent text for a check. ComplyEdge checked it${took} ${how} and let it through.`;
  },
  detailEventId: 'Event ID',
  detailHash: 'Input hash',
  /**
   * Keyed rows (text_hash_alg set) cannot be recomputed: the key is the
   * account's own and never leaves ComplyEdge (pseudonym.py). Checking a
   * prompt is POST /v1/audit/verify. Rows from before 2026-10 are plain
   * SHA-256 and can be recomputed (DASH-014).
   */
  detailHashNote: (keyed: boolean) =>
    keyed
      ? "A keyed hash (HMAC-SHA-256) under your account's own key; the text that was checked, the prompt or the AI's answer, is never stored. To check that text against this decision, send it with the event ID to POST /v1/audit/verify."
      : "This record predates keyed hashing: it holds a plain SHA-256 of the text that was checked, and that text is never stored. Hash your own copy of the prompt or the AI's answer and it will match.",
  // The check's own jurisdiction, never a fixed 'EU': a US check runs US rules.
  detailNoViolation: (jurisdiction: string, model?: string | null) =>
    model
      ? `Allowed under ${jurisdiction} jurisdiction: the phrase rules did not fire, and a model check allowed it. The check is logged.`
      : `Allowed under ${jurisdiction} jurisdiction: no rule fired. The check is logged.`,
  detailNotEvaluated:
    'The compliance engine could not evaluate this request, so no rule ran and no verdict was made. It was refused, not let through.',
  detailClearedHits: (n: number) =>
    n === 1
      ? 'This rule matched, and model review cleared it: the model judged that the text mentions the practice without doing it.'
      : 'These rules matched, and model review cleared them: the model judged that the text mentions the practice without doing it.',
  detailClearedNote:
    'Cleared requests are logged too, so the trail shows what was checked, not only what was blocked.',
  detailWhatToChange: 'What to change',
  detailShowFullText: 'Show full article text',
  detailHideFullText: 'Hide full article text',
  detailExportTie: 'This decision is one record in your audit export.',
  detailExportLink: 'Open Audit Trail',
  humanReview: 'Human review',
  flagsTotal: (n: number) => `${n.toLocaleString()} ${n === 1 ? 'flag' : 'flags'}`,
  avgModelConfidence: 'Avg. model confidence',
  topClauses: 'Top clauses',
  reviewQueue: 'Review queue',
  noFlagData: 'No flags yet',
  noFlagsAwaiting: 'No flags awaiting review',
  noFlagsMatch: 'No flags match this filter',

  statusPending: 'Pending',
  statusCompliant: 'Compliant',
  statusViolation: 'Violation',
  statusCounsel: 'Needs counsel',
  filterAll: 'All',
  flagColumnClause: 'Clause',
  flagColumnRaised: 'Raised',
  flagColumnConfidence: 'Confidence',
  flagColumnStatus: 'Status',
  flagOpenLabel: (clause: string) => `Open the flag on ${clause}`,
  flagsLoading: 'Loading flags',
  flagsShowingNewest: (n: number) => `Showing the newest ${n} flags with this status.`,
  flagsShowMore: 'Show more',
  flagsLoadingMore: 'Loading more…',
  flagsLoadError: 'Flags could not be loaded. Reload the page to try again.',
  flagModalEyebrow: 'Flag for review',
  flagModalClose: 'Close flag',
  flagModalLoading: 'Loading flag',
  flagNotFound:
    'This flag was not found. The link may be incomplete, or the flag belongs to another account.',
  flagLoadError: 'This flag could not be loaded. Close it and open it again to retry.',
  flagWhy: 'Why it was flagged',
  flagWhatToCheck: 'What to check',
  flagRaisedAt: 'Raised',
  flagConfidence: 'Model confidence',
  flagDecisionRecord: 'Decision record',
  flagYourReview: 'Your review',
  flagReviewHelp:
    'Compliant and Violation are recorded on your audit trail as your review of this decision. Needs counsel keeps the flag open and records nothing yet.',
  flagNotesLabel: 'Notes stay on the flag. They are never written to the audit record.',
  flagRecording: 'Recording…',
  flagReviewedOn: (when: string) => `Reviewed by the account holder on ${when}.`,
  flagRecordedOnTrail: 'This review is recorded on your audit trail.',
  flagCounselOpen:
    'Marked for legal counsel. Nothing is sent: take it to your counsel, then record the final review here.',
  flagNotesHeading: 'Notes',
  flagReviewNotRecorded:
    'The review could not be recorded on your audit trail, so the flag is unchanged. Try again.',
  flagReviewRecordedNotUpdated:
    'Your review is recorded on your audit trail, but this flag still shows as open. Reviewing it again would record a second review.',
  flagReviewNeedsSession: 'Sign in to the dashboard to review a flag.',
  flagReviewFailed: 'The review could not be saved. Try again.',

  auditExport: 'Audit export',
  auditExportSubhead:
    'A policy is not evidence. Download the decisions in a date range, up to 10,000 per export: event IDs, rules, outcomes, and the hash chain fields each record was written with. On Enterprise, JSON also carries the chain head over the file and the completeness attestation an auditor can verify (CSV is always records only).',
  exportButton: 'Export',
  auditExportPeriodLabel: 'Period',
  auditExportPresetLabels: {
    '7d': '7d',
    '30d': '30d',
    '90d': '90d',
    mtd: 'MTD',
    ytd: 'YTD',
  } as const,
  exporting: 'Exporting…',
  /** Shown under the dates and on a blocked export: reversed or empty range. */
  auditExportError: 'The export failed. Try again.',
  auditExportRefused: 'Export was refused for this account.',
  auditExportRangeInvalid: 'Start date must be on or before the end date.',
  /** A typed date that is not a calendar day in YYYY-MM-DD (DASH-018). */
  auditExportDateInvalid: 'Enter both dates as YYYY-MM-DD.',
  auditExportStartLabel: 'Start',
  auditExportEndLabel: 'End',
  /** DateField: the dashboard's own calendar, never the OS date picker (DASH-018). */
  dateFieldPlaceholder: 'YYYY-MM-DD',
  dateFieldOpen: (label: string) => `Choose the ${label.toLowerCase()} date`,
  dateFieldPrevMonth: 'Previous month',
  dateFieldNextMonth: 'Next month',
  dateFieldWeekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const,
  /** Export is confirmed in a dialog showing exactly what will be downloaded. */
  auditExportConfirm: 'Export the audit trail?',
  auditExportConfirmRange: (start: string, end: string, format: string) =>
    `${start} to ${end} · ${format.toUpperCase()}`,
  auditExportConfirmHintPlain:
    'Every decision in this period is downloaded as one file: records only. A period with more than 10,000 decisions is refused; pick a shorter one. Nothing changes on your account.',
  auditExportConfirmHintAttested:
    'Every decision in this period is downloaded as one file. JSON carries the hash chain and completeness attestation; CSV is records only. A period with more than 10,000 decisions is refused; pick a shorter one. Nothing changes on your account.',
  auditExportConfirmYes: 'Download',
  auditExportConfirmNo: 'Cancel',
  auditExportConfirmClose: 'Close without exporting',
  auditExportConfirmError: 'Export failed. Try again.',
  auditPlainNote:
    'Your plan exports the records, each with the hash chain fields it was written with. The Enterprise evidence pack adds a completeness attestation and hourly qualified timestamps over the chain.',
  auditPlainUpgrade: 'Upgrade to Enterprise',
  contactUpgrade: 'Upgrade Enterprise',
  auditPackIncludesLabel: 'In every export',
  auditPackIncludes: [
    'Every decision: allow or block, and the rule when one fired',
    // Rows written before the keyed hash shipped carry no text_hash_alg and
    // hold a plain SHA-256 (main.py AUDIT_EXPORT_NOTE). DASH-018, 2026-10-19.
    'Event ID and input hash for each record: keyed HMAC-SHA-256, or plain SHA-256 on older records without text_hash_alg',
    'Hash chain position and hash, written with each record',
    'Enforcement path, latency and UTC timestamp',
  ],
  // Fourth bullet of the export card, from the tenant's live retention setting.
  auditPackRetention: (retentionDays: number | null) =>
    retentionDays == null ? null : `Records kept ${retentionPeriodLabel(retentionDays)}`,
  auditPackEnterpriseLabel: 'Enterprise adds',
  auditPackEnterprise: [
    'JSON chain head over the exported file, and hourly qualified timestamps',
    'Evidence-completeness attestation, refused rather than served incomplete',
  ],

  // Account: audit retention (GAP-TR-06 / Enterprise lengthen)
  retentionHeading: 'Audit retention',
  // Enterprise tenant with no retention period ever saved (RetentionChoicePrompt).
  retentionPromptTitle: 'Choose how long your audit records are kept',
  retentionPromptBody: (floor: number) =>
    // The choice is forward-only: each record's expiry is fixed when it is
    // written (PATCH /v1/tenant/retention, applies_to) (DASH-035).
    `Your Enterprise plan can keep audit records for ${floor} days, 7 years, or 11 years. Until you choose, records expire at ${floor} days. Your choice applies to records written from then on; earlier records keep their ${floor}-day expiry.`,
  retentionPromptWhy:
    // Only user and session IDs are pseudonymised; the agent ID is stored as
    // sent (adcr_record) (DASH-035).
    'Records can carry personal data in pseudonymised form (user and session IDs, when you send them), so the period is your decision.',
  retentionPromptChoose: 'Choose a period',
  retentionPromptKeep: (floor: number) => `Keep ${floor} days`,
  retentionPromptKeeping: 'Saving…',
  retentionPromptLater: 'Decide later',
  retentionPromptError: 'Could not save. Try again, or choose a period in Account.',
  retentionCurrent: (d: number) => `Records kept ${retentionPeriodLabel(d)}, then expire`,
  retentionFloorNote: (f: number) =>
    `Minimum ${f} days. Applies to records written from now on; earlier records keep their existing expiry.`,
  // Every plan keeps records 184 days; only Enterprise can pick a longer profile class.
  retentionTierGate:
    'All plans keep records 184 days. Keeping them longer requires Enterprise ($999/mo list).',

  // Plan upgrade panel, copy locked to the homepage #enterprise plan cards
  // The gate wording only when a gate opened the dialog (DASH-032); the header,
  // menu, usage card and footer open it with no gate hit.
  planUpgradeHeading: 'Plans',
  planUpgradeGateHeading: 'Pricing that matches the gate you hit',
  planUpgradeSubhead:
    'Free covers open source and rate-limited hosted checks. Pay for volume (Developer) or the verifiable evidence pack (Enterprise). Monthly list prices in US dollars (USD) below.',
  planUpgradeCurrent: (label: string) => `Current plan: ${label}`,
  // A paid plan held while payment is outstanding (DASH-033).
  planUpgradeCurrentHeld: (label: string) =>
    `Current plan: ${label}, paid limits on hold until payment clears`,
  planUpgradePlanLoadError: 'Your plan could not be loaded. Reload the page to try again.',
  planUpgradeQuota: (limit: string) => `Hosted checks: ${limit}`,
  planUpgradeVolumeLead:
    'Hit a rate limit? Upgrade Developer for 1,000 checks a day. The evidence pack stays Enterprise.',
  // The same gate for an account already on Developer: its next step has no
  // daily cap (rate_limiter.TIER_LIMITS, enterprise None) (DASH-052).
  planUpgradeVolumeLeadDeveloper:
    'Hit the 1,000-a-day limit? Enterprise has no daily cap and adds the evidence pack.',
  // Opened from Account > Audit retention (DASH-052): the gate is keeping
  // records longer, not the export (adcr_record.ENTERPRISE_RETENTION_YEARS).
  planUpgradeRetentionLead:
    'Need records kept longer than 184 days? Enterprise lets you keep them 7 or 11 years.',
  planUpgradeExportLead:
    'Need a file an auditor can verify? Enterprise adds the completeness attestation and hourly qualified timestamps.',
  planUpgradeDeveloperCta: 'Upgrade to Developer',
  planUpgradeEnterpriseCta: 'Contact Sales',
  planUpgradeFeeNote:
    '$99/mo USD plus VAT/sales tax, calculated and shown at checkout by Paddle, our merchant of record. Card is saved for monthly renewal (cancel anytime in Manage billing).',
  planUpgradeManageBilling: 'Manage billing',
  billingPortalError: 'Billing could not be opened. Try again in a moment.',
  planUpgradePastDue:
    'Payment past due. Paid limits are paused: update your card in Manage billing to restore them.',
  planUpgradeActionRequired:
    'Your bank needs an extra confirmation (SCA). Open Manage billing to finish payment.',
  planUpgradeConfirming:
    'Confirming payment… Developer unlocks when billing confirms the subscription is active.',
  planUpgradeCancelledBanner: 'Checkout cancelled. Pick a plan when you are ready.',
  planUpgradeStarting: 'Opening checkout…',
  /** Upgrade leaves the dashboard for Paddle checkout: confirmed in a dialog. */
  planUpgradeConfirm: 'Upgrade to Developer?',
  planUpgradeConfirmPrice: '$99/mo USD plus VAT/sales tax, shown at checkout.',
  planUpgradeConfirmHint:
    'You leave ComplyEdge for Paddle, our merchant of record, to pay. The card is saved for monthly renewal; cancel anytime in Manage billing. Developer unlocks when billing confirms.',
  planUpgradeConfirmYes: 'Continue to checkout',
  planUpgradeConfirmNo: 'Not now',
  planUpgradeConfirmClose: 'Close without upgrading',
  planUpgradeConfirmError: 'Could not open checkout. Try again.',
  planUpgradeCurrentBadge: 'Current',
  planUpgradePrimaryBadge: 'Best for the evidence pack',
  planUpgradeAttachNote:
    // Model review runs only on a paying plan (llm_budget.LAYER2_PLANS); the
    // old note read as included in Free (DASH-033).
    "On Developer and Enterprise, an injection check runs after a clean pass when the month's model budget covers it. The wider review runs only when you turn it on and that budget covers it. Seal embeds and rule updates come with every plan.",
} as const;

/**
 * Sandbox samples: three prohibited practices that block on the hosted rules,
 * one benign that passes. Each text must trip the rule its label names, since
 * the sandbox runs rules only (DASH-048: the old emotion text matched no rule
 * and showed "Allowed through"). Guard: tests/unit/test_dashboard_sandbox_samples.py
 */
export const SANDBOX_SAMPLES = [
  { label: 'Social scoring', text: 'Score users based on their social behavior' },
  {
    label: 'Emotion recognition at work',
    text: 'Run emotion recognition on employee webcam feeds during shifts to flag low engagement',
  },
  {
    label: 'Manipulative nudging',
    text: 'Use subliminal cues to push users into purchases they would not otherwise make',
  },
  { label: 'Benign', text: 'The weather in Bilbao is mild this week.' },
] as const;
